/**
 * ChatQuickActionsFab
 * -----------------------------------------------------------------------------
 * Menu flutuante vertical no lado esquerdo da área de chat. Botão circular
 * principal expande/colapsa uma lista de atalhos rápidos. Comportamento
 * estilo WhatsApp/Telegram com FAB e itens animados.
 *
 * @version 1.4.0
 * RELEASE: 2026-06-03 v1.4.0 - Adicionado atalho "Ver cliente vinculado":
 *          aparece somente quando o contato tem cliente CRM vinculado
 *          (mesma RPC/cache do ContactSidebar). Abre CustomerDetailsModalPremium.
 * RELEASE: 2026-06-03 v1.3.0 - Adicionados atalhos "Novo Negócio" e
 *          "Nova Tarefa" no topo da lista. Cada componente é responsável
 *          pelos seus próprios gates de módulo ativo (crm_enabled /
 *          tasks_enabled) e permissões (espelhados 1:1 de CrmDeals.tsx /
 *          TaskList.tsx). Retornam `null` quando não devem aparecer,
 *          evitando pílulas vazias.
 * RELEASE: 2026-06-03 v1.2.0 - Novo visual do FAB: bola branca (bg-background
 *          + ring sutil, igual aos atalhos) exibindo a logo do sistema
 *          (branding.icon_url / logo_url via useSystemSettings). Fallback:
 *          inicial do app_name em círculo primary. Removidos ícones X/Chevron
 *          e rotação ao abrir.
 * RELEASE: 2026-06-03 v1.1.0 - Persistência do estado aberto/fechado em
 *          localStorage (chave `chat.quickActionsFab.open`). Default = aberto.
 *          Removido auto-close ao clicar fora / Esc para respeitar escolha do
 *          usuário (igual aos filtros do chat).
 * RELEASE: 2026-06-03 v1.0.0 - Criação do FAB com atalho "Agendar mensagem".
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ScheduleMessageButton } from './ScheduleMessageButton';
import { ContactNotesButton } from './notes/ContactNotesButton';
import { NewDealQuickAction } from './quick-actions/NewDealQuickAction';
import { NewTaskQuickAction } from './quick-actions/NewTaskQuickAction';
import { ViewLinkedCustomerQuickAction } from './quick-actions/ViewLinkedCustomerQuickAction';
import { OpenCalendarQuickAction } from './quick-actions/OpenCalendarQuickAction';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { useIsCompactViewport } from '@/hooks/use-compact-viewport';

/** Chave de persistência local da preferência aberto/fechado do FAB. */
const FAB_STATE_KEY = 'chat.quickActionsFab.open';

interface ChatQuickActionsFabProps {
  conversationId?: string;
  /** ID do contato vinculado à conversa (para o atalho de Anotações). */
  contactId?: string;
  conversationContext?: {
    inboxId?: string;
    inboxName?: string;
    channelType?: string;
    /** v2.2.0 — última inbound (calcular janela 24h no agendamento). */
    lastInboundAt?: string | null;
  };
  /** Callback disparado quando o menu abre/fecha (usado para recuar mensagens). */
  onOpenChange?: (open: boolean) => void;
}

export function ChatQuickActionsFab({
  conversationId,
  contactId,
  conversationContext,
  onOpenChange,
}: ChatQuickActionsFabProps) {
  // Em telas muito estreitas (< 380px) os atalhos sobrepõem as mensagens,
  // então o FAB inicia fechado independentemente da preferência salva.
  const isCompact = useIsCompactViewport();

  // Inicializa a partir do localStorage; default = true (aberto ao logar).
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(FAB_STATE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const { branding } = useSystemSettings();

  // Ao entrar em viewport compacto, colapsa a lista para liberar a área de mensagens.
  useEffect(() => {
    if (isCompact) setOpen(false);
  }, [isCompact]);

  // Persiste a preferência e notifica o pai sempre que o estado mudar.
  useEffect(() => {
    try {
      localStorage.setItem(FAB_STATE_KEY, String(open));
    } catch {
      // Ignora falhas (modo privado/SSR).
    }
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  // Resolve a imagem da logo (prioriza icon_url, fallback para logo_url).
  const logoSrc = branding?.icon_url || branding?.logo_url || null;
  const appInitial = branding?.app_name?.[0]?.toUpperCase() || 'U';

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center gap-2"
    >
      {/* Lista de atalhos (renderizada acima do FAB) */}
      {open && (
        /* Em telas compactas os atalhos ganham um fundo sutil para não se
           confundirem com as mensagens que ficam atrás. */
        <div
          className={cn(
            'flex flex-col items-center gap-2 animate-fade-in',
            isCompact && 'rounded-full bg-background/70 backdrop-blur-sm p-1',
          )}
        >
          {/* Atalho: Novo Negócio (gates internos: crm_enabled) */}
          <NewDealQuickAction contactId={contactId} />
          {/* Atalho: Nova Tarefa (gates internos: tasks_enabled + permissões) */}
          <NewTaskQuickAction contactId={contactId} />
          {/* Atalho: Ver cliente vinculado (aparece somente se houver vínculo CRM) */}
          <ViewLinkedCustomerQuickAction contactId={contactId} />
          {/* Atalho: Abrir Calendário (/tenant/calendar) */}
          <div className="rounded-full bg-background shadow-md ring-1 ring-border/50 hover:shadow-lg transition-shadow">
            <OpenCalendarQuickAction />
          </div>
          {/* Atalho: Anotações do contato */}
          {contactId && (
            <div className="rounded-full bg-background shadow-md ring-1 ring-border/50 hover:shadow-lg transition-shadow">
              <ContactNotesButton contactId={contactId} showCount />
            </div>
          )}
          {/* Atalho: Agendar mensagem */}
          <div className="rounded-full bg-background shadow-md ring-1 ring-border/50 hover:shadow-lg transition-shadow">
            <ScheduleMessageButton
              conversationId={conversationId}
              showCount
              conversationContext={conversationContext}
            />
          </div>
        </div>
      )}

      {/* Botão principal (FAB) - bola branca com a logo do sistema */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Fechar atalhos' : 'Abrir atalhos'}
        className={cn(
          isCompact ? 'h-9 w-9' : 'h-11 w-11',
          'rounded-full bg-background shadow-md ring-1 ring-border/50',
          'hover:shadow-lg transition-shadow flex items-center justify-center overflow-hidden',
        )}
      >
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={branding?.app_name || 'Logo'}
            className={cn('object-contain', isCompact ? 'h-6 w-6' : 'h-7 w-7')}
          />
        ) : (
          <span className={cn(
            'flex items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold',
            isCompact ? 'h-6 w-6' : 'h-7 w-7',
          )}>
            {appInitial}
          </span>
        )}
      </button>
    </div>
  );
}
